/**
 * YouTube Platform Media Restrictions
 * Based on official YouTube Data API v3 specifications
 *
 * @see https://developers.google.com/youtube/v3/docs/videos#resource
 * @see https://support.google.com/youtube/answer/1722171
 * @see https://support.google.com/youtube/answer/10059070 (Shorts)
 */

export const YOUTUBE_RESTRICTIONS = {
  video: {
    maxFileSize: 256 * 1024 * 1024 * 1024, // 256 GB (or 128 GB depending on account verification)
    allowedFormats: [
      'video/mp4',
      'video/mpeg',
      'video/quicktime', // .mov
      'video/x-msvideo', // .avi
      'video/x-ms-wmv', // .wmv
      'video/x-flv', // .flv
      'video/webm',
      'video/3gpp', // .3gp
    ],
    maxDuration: 12 * 60 * 60, // 12 hours (varies by account verification)
    minDuration: 1, // 1 second
    recommendedResolution: { width: 1920, height: 1080 }, // 1080p
    supportedResolutions: [
      { width: 7680, height: 4320 }, // 8K
      { width: 3840, height: 2160 }, // 4K
      { width: 2560, height: 1440 }, // 1440p
      { width: 1920, height: 1080 }, // 1080p
      { width: 1280, height: 720 }, // 720p
      { width: 854, height: 480 }, // 480p
      { width: 640, height: 360 }, // 360p
      { width: 426, height: 240 }, // 240p
    ],
    aspectRatio: {
      standard: 16 / 9, // Recommended for regular videos
      vertical: 9 / 16, // For Shorts
    },
    frameRate: {
      standard: [24, 25, 30, 48, 50, 60], // Common frame rates
    },
  },
  shorts: {
    // YouTube Shorts are vertical videos ≤60 seconds
    maxFileSize: 256 * 1024 * 1024 * 1024, // Same as regular video
    allowedFormats: ['video/mp4', 'video/quicktime', 'video/webm'],
    maxDuration: 60, // 60 seconds
    minDuration: 1, // 1 second
    aspectRatio: 9 / 16, // Vertical (required for Shorts)
    recommendedResolution: { width: 1080, height: 1920 }, // 1080x1920 vertical
  },
  metadata: {
    title: {
      maxLength: 100, // characters
      required: true,
    },
    description: {
      maxLength: 5000, // characters
      required: false,
    },
    tags: {
      maxTotalLength: 500, // total characters across all tags
      maxPerTag: 30, // characters per individual tag
      maxCount: 500, // maximum number of tags (limited by total character count)
      required: false,
    },
  },
  thumbnail: {
    allowedFormats: ['image/jpeg', 'image/png'],
    maxFileSize: 2 * 1024 * 1024, // 2 MB
    recommendedResolution: { width: 1280, height: 720 }, // 1280x720 (16:9 ratio)
    minResolution: { width: 640, height: 360 },
  },
  general: {
    // YouTube does not support image-only posts
    supportsImages: false,
    supportsDocuments: false,
    requiresVideo: true, // All posts must be videos
  },
  privacy: {
    // Privacy settings available for videos
    levels: ['public', 'unlisted', 'private'],
  },
} as const

export type YouTubeRestrictions = typeof YOUTUBE_RESTRICTIONS
